'use client';

import { useEffect, useState, useTransition, useRef, useCallback } from 'react';
import type { ChangeEvent, FormEvent, KeyboardEvent } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, AlertCircle, ShieldCheck, KeyRound, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { AuthShell } from '@/components/layout/AuthShell';
import { MfaQrCode } from '@/components/shared/MfaQrCode';
import { ProviderLogo } from '@/components/shared/ProviderLogo';
import { toast } from '@/hooks/use-toast';
import { LoginService, LoginError } from '@/services/login';
import type {
  MfaSetupResponse,
  MfaVerifyRequest,
  PendingAuthFlow,
  PendingAuthResponse,
  SsoProviderId,
  User,
} from '@/services/login';
import { primeCachedUser } from '@/services/login/auth-helpers';

/* ─────────────────────── Types ─────────────────────── */

interface LoginFormState {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface MfaState {
  pending: boolean;
  flow: PendingAuthFlow | null;
  token: string;
  userId: string;
  companyId: string;
  digits: string[];
  useRecoveryCode: boolean;
  recoveryCode: string;
  setup: MfaSetupResponse | null;
  recoveryCodes: string[];
  authenticatedUser: User | null;
}

interface LoginScreenProps {
  hasSsoError?: boolean;
}

const INITIAL_FORM: LoginFormState = { email: '', password: '', rememberMe: false };
const INITIAL_MFA: MfaState = {
  pending: false,
  flow: null,
  token: '',
  userId: '',
  companyId: '',
  digits: ['', '', '', '', '', ''],
  useRecoveryCode: false,
  recoveryCode: '',
  setup: null,
  recoveryCodes: [],
  authenticatedUser: null,
};
const OTP_LENGTH = 6;
const SSO_PENDING_AUTH_STORAGE_KEY = 'moonitor:ssoPendingAuth';
const SSO_LOGIN_ERROR_MESSAGE = 'SSO login could not be completed. Please try again or use your password.';

/* ─────────────────────── Helpers ─────────────────────── */

function isPendingAuthResponse(data: unknown): data is PendingAuthResponse {
  return typeof data === 'object' && data !== null && (data as PendingAuthResponse).pendingAuth === true;
}

function blankDigits(): string[] {
  return ['', '', '', '', '', ''];
}

function buildMfaState(result: PendingAuthResponse): MfaState {
  return {
    pending: true,
    flow: result.flow,
    token: result.pendingAuthToken,
    userId: result.userId,
    companyId: result.companyId,
    digits: blankDigits(),
    useRecoveryCode: false,
    recoveryCode: '',
    setup: null,
    recoveryCodes: [],
    authenticatedUser: null,
  };
}

/* ─────────────────────── Component ─────────────────────── */

export function LoginScreen({ hasSsoError = false }: LoginScreenProps) {
  const router = useRouter();
  const [formState, setFormState] = useState<LoginFormState>(INITIAL_FORM);
  const [mfa, setMfa] = useState<MfaState>(INITIAL_MFA);
  const [error, setError] = useState<string | null>(hasSsoError ? SSO_LOGIN_ERROR_MESSAGE : null);
  const [showPassword, setShowPassword] = useState(false);
  const [isPending, startTransition] = useTransition();
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const pendingSsoHydratedRef = useRef(false);

  useEffect(() => {
    if (pendingSsoHydratedRef.current) {
      return;
    }
    pendingSsoHydratedRef.current = true;

    const rawPendingAuth = window.sessionStorage.getItem(SSO_PENDING_AUTH_STORAGE_KEY);
    if (!rawPendingAuth) {
      return;
    }
    window.sessionStorage.removeItem(SSO_PENDING_AUTH_STORAGE_KEY);

    try {
      const parsedPendingAuth = JSON.parse(rawPendingAuth);
      if (!isPendingAuthResponse(parsedPendingAuth)) {
        window.setTimeout(() => setError('SSO login could not continue. Please start again.'), 0);
        return;
      }

      window.setTimeout(() => {
        setError(null);
        setMfa(buildMfaState(parsedPendingAuth));
        if (parsedPendingAuth.flow === 'enroll_mfa') {
          void LoginService.getMfaSetup({
            pendingAuthToken: parsedPendingAuth.pendingAuthToken,
          })
            .then((setup) => setMfa((prev) => ({ ...prev, setup })))
            .catch(() => {
              setError('MFA setup could not be loaded. Please start SSO again.');
              setMfa(INITIAL_MFA);
            });
        }
        setTimeout(() => otpRefs.current[0]?.focus(), 50);
      }, 0);
    } catch {
      window.setTimeout(() => setError('SSO login could not continue. Please start again.'), 0);
    }
  }, []);

  /* ── Form handlers ── */

  const handleChange = (field: keyof LoginFormState) => (event: ChangeEvent<HTMLInputElement>) => {
    setFormState((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!formState.email || !formState.password) {
      setError('Email and password are required.');
      return;
    }

    setError(null);
    startTransition(async () => {
      try {
        const result = await LoginService.login({
          username: formState.email,
          password: formState.password,
          rememberMe: formState.rememberMe,
        });

        if (isPendingAuthResponse(result)) {
          setMfa(buildMfaState(result));
          if (result.flow === 'enroll_mfa') {
            const setup = await LoginService.getMfaSetup({
              pendingAuthToken: result.pendingAuthToken,
            });
            setMfa((prev) => ({ ...prev, setup }));
          }
          setTimeout(() => otpRefs.current[0]?.focus(), 50);
          return;
        }

        // If the user must change password, redirect to the change-password screen
        if (result.user?.firstAccess) {
          primeCachedUser(result.user);
          router.push('/change-password');
          return;
        }

        if (result.user) {
          primeCachedUser(result.user);
        }
        toast({ title: 'Login successful', description: 'Redirecting you to the dashboard.' });
        router.push('/dashboard');
      } catch (err) {
        if (err instanceof LoginError) {
          const errorCode = err.getErrorCode();
          if (errorCode === 'USER_NOT_FOUND' || errorCode === 'INVALID_CREDENTIALS') {
            setError('Invalid email or password. Please try again.');
          } else if (errorCode === 'BACKEND_UNAVAILABLE') {
            setError('Unable to connect to server. Please try again later.');
          } else {
            setError(err.message || 'Login failed. Please try again.');
          }
        } else {
          setError('An unexpected error occurred. Please try again.');
        }
      }
    });
  };

  /* ── MFA handlers ── */

  const handleOtpChange = useCallback((index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;
    setMfa((prev) => {
      const digits = [...prev.digits];
      digits[index] = value;
      return { ...prev, digits };
    });
    if (value && index < OTP_LENGTH - 1) {
      otpRefs.current[index + 1]?.focus();
    }
  }, []);

  const handleOtpKeyDown = useCallback((index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !mfa.digits[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  }, [mfa.digits]);

  const handleOtpPaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    setMfa((prev) => {
      const digits = [...prev.digits];
      for (let i = 0; i < OTP_LENGTH; i++) {
        digits[i] = pasted[i] || '';
      }
      return { ...prev, digits };
    });
    const focusIdx = Math.min(pasted.length, OTP_LENGTH - 1);
    otpRefs.current[focusIdx]?.focus();
  }, []);

  const setMfaVerificationMode = useCallback((useRecoveryCode: boolean) => {
    setError(null);
    setMfa((prev) => ({
      ...prev,
      useRecoveryCode,
      digits: useRecoveryCode ? blankDigits() : prev.digits,
      recoveryCode: useRecoveryCode ? prev.recoveryCode : '',
    }));
    if (!useRecoveryCode) {
      setTimeout(() => otpRefs.current[0]?.focus(), 50);
    }
  }, []);

  const completeAuthenticatedFlow = (user: User) => {
    if (user.firstAccess) {
      primeCachedUser(user);
      router.push('/change-password');
      return;
    }
    primeCachedUser(user);
    toast({ title: 'Login successful', description: 'Redirecting you to the dashboard.' });
    router.push('/dashboard');
  };

  const handleVerifyMfa = () => {
    const code = mfa.digits.join('');
    const recoveryCode = mfa.recoveryCode.trim();
    if (mfa.useRecoveryCode && !recoveryCode) {
      setError('Please enter a recovery code.');
      return;
    }
    if (!mfa.useRecoveryCode && code.length !== OTP_LENGTH) {
      setError('Please enter the full 6-digit code.');
      return;
    }

    setError(null);
    startTransition(async () => {
      try {
        const payload: MfaVerifyRequest = {
          pendingAuthToken: mfa.token,
          rememberMe: formState.rememberMe,
        };
        if (mfa.useRecoveryCode) {
          payload.recoveryCode = recoveryCode;
        } else {
          payload.totpCode = code;
        }
        const result = await LoginService.verifyMfa(payload);
        completeAuthenticatedFlow(result.user);
      } catch (err) {
        if (err instanceof LoginError) {
          const errorRef = err.getErrorRef();
          if (errorRef === 'mfa_code' || errorRef === 'totp_code') {
            setError('Invalid verification code. Please try again.');
          } else if (errorRef === 'pending_auth_token' || errorRef === 'mfa_challenge') {
            setError('Verification session expired. Please log in again.');
            setMfa(INITIAL_MFA);
          } else {
            setError(err.message || 'Verification failed. Please try again.');
          }
        } else {
          setError('An unexpected error occurred. Please try again.');
        }
      }
    });
  };

  const handleEnrollMfa = () => {
    const code = mfa.digits.join('');
    if (code.length !== OTP_LENGTH) {
      setError('Please enter the full 6-digit code.');
      return;
    }

    setError(null);
    startTransition(async () => {
      try {
        const result = await LoginService.enrollMfa({
          pendingAuthToken: mfa.token,
          totpCode: code,
          rememberMe: formState.rememberMe,
        });
        setMfa((prev) => ({
          ...prev,
          authenticatedUser: result.user,
          recoveryCodes: result.recoveryCodes,
        }));
      } catch (err) {
        if (err instanceof LoginError) {
          const errorRef = err.getErrorRef();
          if (errorRef === 'totp_code' || errorRef === 'mfa_code') {
            setError('Invalid verification code. Please try again.');
          } else if (errorRef === 'pending_auth_token' || errorRef === 'mfa_challenge') {
            setError('Setup session expired. Please log in again.');
            setMfa(INITIAL_MFA);
          } else {
            setError(err.message || 'Enrollment failed. Please try again.');
          }
        } else {
          setError('An unexpected error occurred. Please try again.');
        }
      }
    });
  };

  const handleMfaSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (mfa.flow === 'enroll_mfa') {
      handleEnrollMfa();
      return;
    }
    handleVerifyMfa();
  };

  const handleBack = () => {
    setMfa(INITIAL_MFA);
    setError(null);
  };

  const handleProviderLogin = (providerId: SsoProviderId) => {
    setError(null);
    startTransition(async () => {
      try {
        const email = formState.email.trim();
        const result = await LoginService.startSso({
          providerId,
          returnUrl: '/dashboard',
          loginHint: email.includes('@') ? email : undefined,
          rememberMe: formState.rememberMe,
        });
        window.location.assign(result.authorizationUrl);
      } catch {
        setError('SSO login could not be started. Please try again or use your password.');
      }
    });
  };

  /* ── Error banner ── */

  const errorBanner = error ? (
    <div role="alert" className="flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{error}</span>
    </div>
  ) : null;

  /* ── MFA step ── */

  if (mfa.recoveryCodes.length > 0 && mfa.authenticatedUser) {
    return (
      <AuthShell
        title="Recovery Codes"
        description="Store these one-time codes somewhere private"
        footer={null}
      >
        <div className="grid gap-4">
          <div className="grid grid-cols-2 gap-2 rounded-md border bg-muted/30 p-3 font-mono text-sm">
            {mfa.recoveryCodes.map((code) => (
              <span key={code}>{code}</span>
            ))}
          </div>
          <Button
            className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-bold"
            onClick={() => completeAuthenticatedFlow(mfa.authenticatedUser as User)}
          >
            Continue
          </Button>
        </div>
      </AuthShell>
    );
  }

  if (mfa.pending) {
    const isEnrollment = mfa.flow === 'enroll_mfa';
    return (
      <AuthShell
        title={isEnrollment ? 'Register MFA' : 'Verify MFA'}
        description={isEnrollment ? 'Add Moonitor to your authenticator app' : 'Enter the code from your authenticator app'}
        footer={
          <>
            Need access?{' '}
            <Link href="/#contact" className="underline font-semibold" prefetch={false}>
              Contact us
            </Link>
          </>
        }
      >
        <form className="grid gap-4" onSubmit={handleMfaSubmit} noValidate>
          <div className="flex items-center justify-center gap-1 text-muted-foreground">
            {isEnrollment ? <KeyRound className="h-5 w-5" /> : <ShieldCheck className="h-5 w-5" />}
            <span className="text-sm">{isEnrollment ? 'Enrollment required' : 'Verification required'}</span>
          </div>
          {isEnrollment && mfa.setup ? (
            <div className="grid gap-2 rounded-md border bg-muted/30 p-3">
              <MfaQrCode value={mfa.setup.otpauthUri} label="Moonitor MFA setup QR code" />
              <Label htmlFor="manual-entry-key">Manual setup key</Label>
              <Input
                id="manual-entry-key"
                value={mfa.setup.manualEntryKey}
                readOnly
                className="font-mono text-xs"
              />
              <Label htmlFor="otpauth-uri">Authenticator URI</Label>
              <Textarea
                id="otpauth-uri"
                value={mfa.setup.otpauthUri}
                readOnly
                className="min-h-20 resize-none break-all font-mono text-xs"
              />
            </div>
          ) : null}
          {!mfa.useRecoveryCode ? (
            <div className="grid gap-3">
              <div className="flex justify-center gap-2">
                {mfa.digits.map((digit, i) => (
                  <Input
                    key={i}
                    ref={(el) => { otpRefs.current[i] = el; }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(i, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(i, e)}
                    onPaste={i === 0 ? handleOtpPaste : undefined}
                    className="h-12 w-10 text-center text-lg"
                    autoComplete="one-time-code"
                  />
                ))}
              </div>
              {!isEnrollment ? (
                <Button
                  type="button"
                  variant="link"
                  className="h-auto px-0 text-sm text-muted-foreground"
                  onClick={() => setMfaVerificationMode(true)}
                  disabled={isPending}
                >
                  Don&apos;t have access to Authenticator now?
                </Button>
              ) : null}
            </div>
          ) : (
            <div className="grid gap-2">
              <Label htmlFor="recoveryCode">Recovery code</Label>
              <Input
                id="recoveryCode"
                value={mfa.recoveryCode}
                onChange={(event) => setMfa((prev) => ({ ...prev, recoveryCode: event.target.value }))}
                autoComplete="one-time-code"
              />
              <Button
                type="button"
                variant="link"
                className="h-auto justify-self-start px-0 text-sm text-muted-foreground"
                onClick={() => setMfaVerificationMode(false)}
                disabled={isPending}
              >
                Use authenticator code instead
              </Button>
            </div>
          )}
          {errorBanner}
          <Button
            type="submit"
            className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-bold"
            disabled={isPending}
          >
            {isPending ? 'Checking…' : isEnrollment ? 'Activate MFA' : 'Verify'}
          </Button>
          <Button type="button" variant="ghost" className="w-full" onClick={handleBack} disabled={isPending}>
            Back to login
          </Button>
        </form>
      </AuthShell>
    );
  }

  /* ── Login form ── */

  return (
    <AuthShell
      title="Login"
      description="Enter your email below to login to your account"
      footer={
        <>
          Need access?{' '}
          <Link href="/#contact" className="underline font-semibold" prefetch={false}>
            Contact us
          </Link>
        </>
      }
    >
      <form className="grid gap-4" onSubmit={handleSubmit} noValidate>
        <div className="grid gap-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="m@example.com"
            value={formState.email}
            onChange={handleChange('email')}
            required
          />
        </div>
        <div className="grid gap-2">
          <div className="flex items-center">
            <Label htmlFor="password">Password</Label>
            <Link href="/forgot-password" className="ml-auto inline-block text-sm underline text-muted-foreground" prefetch={false}>
              Forgot your password?
            </Link>
          </div>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              value={formState.password}
              onChange={handleChange('password')}
              className="pr-10"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((p) => !p)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="rememberMe"
            checked={formState.rememberMe}
            onCheckedChange={(checked) => setFormState((prev) => ({ ...prev, rememberMe: checked === true }))}
          />
          <Label htmlFor="rememberMe" className="text-sm font-normal cursor-pointer">
            Remember me for 30 days
          </Label>
        </div>
        {errorBanner}
        <Button type="submit" className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-bold" disabled={isPending}>
          {isPending ? 'Logging in…' : 'Login'}
        </Button>
        <div className="grid gap-2 sm:grid-cols-2">
          <Button
            type="button"
            variant="outline"
            className="w-full gap-2"
            onClick={() => handleProviderLogin('google')}
            disabled={isPending}
          >
            <ProviderLogo providerId="google" className="h-4 w-4" />
            Google
          </Button>
          <Button
            type="button"
            variant="outline"
            className="w-full gap-2"
            onClick={() => handleProviderLogin('microsoft')}
            disabled={isPending}
          >
            <Building2 className="h-4 w-4" />
            Microsoft
          </Button>
        </div>
      </form>
    </AuthShell>
  );
}
